import { useMemo, useState } from 'react';
import { Column, useTable, useSortBy, useGlobalFilter, useFilters, usePagination } from 'react-table';
import { Box, useDisclosure } from '@chakra-ui/react';
import { useGetClientAliasesQuery, useLazyExportClientAliasesQuery } from 'core/API/productsApi';
import TagsCell from 'plugins/DITable/Cells/tagsCell';
import DefaultColumnFilter from 'plugins/DITable/filters/DefaultColumnFilter';
import { linkStyle } from 'shared/styles';
import { useGetCompanyInfoSelector, useGetUserInfoSelector } from 'core/redux/selectors/authSelectors';
import { useTranslation } from 'helpers/languages/translations';
import { aliasRow } from './types';

const useAliasContainer = () => {
    const translate = useTranslation();

    const [currentAlias, setCurrentAlias] = useState<{ mainAlias: string; aliases: string[] } | null>(null);

    const { data: aliasesData, isError: isGetAliasError, isLoading } = useGetClientAliasesQuery();
    const [exportAliases, { isLoading: isExportLoading }] = useLazyExportClientAliasesQuery();

    const { isOpen: isOpenEdit, onOpen: onOpenEdit, onClose: onCloseEdit } = useDisclosure();
    const { isOpen: isOpenDelete, onOpen: onOpenDelete, onClose: onCloseDelete } = useDisclosure();
    const { isOpen: isOpenAdd, onOpen: onOpenAdd, onClose: onCloseAdd } = useDisclosure();

    const companyInfo = useGetCompanyInfoSelector();
    const userInfo = useGetUserInfoSelector();
    const readonly = !userInfo?.user_groups?.includes(20);

    const data: Readonly<aliasRow[]> = useMemo(() => {
        const aliasesRows: aliasRow[] = [];

        if (!isLoading && !isGetAliasError && aliasesData && aliasesData.aliases) {
            Object.entries(aliasesData.aliases).forEach(([key, aliases]) => {
                if (aliases.length > 0)
                    aliasesRows.push({
                        mainAlias: key,
                        aliases,
                    });
            });
        }

        return aliasesRows;
    }, [isLoading, isGetAliasError, aliasesData]);

    const columns: Readonly<Column<aliasRow>[]> = useMemo(() => {
        const handleClickAlias = (alias: aliasRow) => {
            setCurrentAlias(alias);
            onOpenEdit();
        };

        return [
            {
                Header: translate('alias.name_split') as string,
                accessor: 'mainAlias',
                width: '30%',
                disableFilters: true,
                Cell: ({
                    cell: {
                        row: { original },
                    },
                }) => {
                    return (
                        <Box onClick={() => handleClickAlias(original)} {...linkStyle}>
                            {original.mainAlias}
                        </Box>
                    );
                },
            },
            {
                Header: translate('alias.aliases') as string,
                accessor: 'aliases',
                Cell: TagsCell,
                disableFilters: true,
            },
        ];
    }, []);

    const defaultColumn = useMemo(
        () => ({
            Filter: DefaultColumnFilter,
        }),
        [],
    );

    const tableInstance = useTable(
        {
            initialState: { pageIndex: 0 }, // Pass your initial state
            autoResetPage: false, // Disable auto-reset of page index
            getAutoResetPage: () => false, // Override the default behavior to disable auto-reset
            columns: columns as Readonly<Column<any>[]>,
            data,
            defaultColumn,
        },
        useFilters,
        useGlobalFilter,
        useSortBy,
        usePagination,
    );

    const props = useMemo(
        () => ({
            isLoading,
            isGetAliasError,
            aliasesData,
            tableInstance,
            companyInfo,
            isExportLoading,
            isOpenAdd,
            currentAlias,
            isOpenEdit,
            isOpenDelete,
            readonly,
            onCloseDelete,
            onOpenDelete,
            onCloseEdit,
            setCurrentAlias,
            onCloseAdd,
            exportAliases,
            onOpenAdd,
        }),
        [
            isLoading,
            isGetAliasError,
            aliasesData,
            tableInstance,
            companyInfo,
            isExportLoading,
            isOpenAdd,
            currentAlias,
            isOpenEdit,
            isOpenDelete,
            readonly,
        ],
    );

    return props;
};

export type TAliasProps = ReturnType<typeof useAliasContainer>;
export default useAliasContainer;
